import { FC, useEffect } from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { GlobalModalTypeEnum } from 'enums/entity';

import useAuth from 'store/auth';
import useBranding from 'store/branding';
import useToaster from 'hooks/useToaster';
import useQueryParams from 'hooks/useQueryParams';
import useGlobalModal from 'hooks/useGlobalModal';
import { TranslationKey, translate } from 'utils/translate';
import { RESET_TOKEN_QUERY, VERIFY_EMAIL_QUERY } from 'constant';
import { verifyEmail } from 'api/requests/account';

import SignUpFormModal from '../AuthModals/SignUpFormModal';
import SignInFormModal from '../AuthModals/SignInFormModal';
import SetNewPasswordModal from '../AuthModals/SetNewPasswordModal';
import ForgotPasswordModal from '../AuthModals/ForgotPasswordModal';

import sx from './styles';
import Header from './Header';
import Footer from './Footer';

const Landing: FC = () => {
  const notify = useToaster();
  const [authState] = useAuth();
  const [brandingState] = useBranding();
  const queryParams = useQueryParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { modalType, setModalType, openGlobalModal } = useGlobalModal();
  const emailQuery = queryParams.get(VERIFY_EMAIL_QUERY);

  useEffect(() => {
    if (authState?.error?.text) {
      notify({
        type: 'error',
        message: authState?.error?.text as TranslationKey,
      });
    }
  }, [authState?.error, notify]);

  useEffect(() => {
    if (emailQuery) {
      (async (): Promise<void> => {
        try {
          await verifyEmail({ verifyEmailToken: emailQuery as string });
          notify({
            type: 'success',
            message: 'Your email verified' as TranslationKey,
          });
        } catch (error) {
          console.error(error);
          notify({
            type: 'error',
            message: 'Something went wrong' as TranslationKey,
          });
        }
      })();
    }
  }, [emailQuery, notify]);

  useEffect(() => {
    if (queryParams.get('modal') && queryParams.get('modal') !== modalType) {
      setModalType(queryParams.get('modal') as GlobalModalTypeEnum);
    }
  }, [queryParams, modalType, setModalType]);

  useEffect(() => {
    if (queryParams.get(RESET_TOKEN_QUERY) && queryParams.get('modal') !== GlobalModalTypeEnum.SET_NEW_PASSWORD) {
      openGlobalModal(GlobalModalTypeEnum.SET_NEW_PASSWORD);
      setModalType(GlobalModalTypeEnum.SET_NEW_PASSWORD);
    }
  }, [openGlobalModal, setModalType, queryParams]);

  const closeModal = (): void => {
    location.search = location.search.replace(`&modal=${queryParams.get('modal')}`, '');
    location.search = location.search.replace(`?modal=${queryParams.get('modal')}`, '');
    location.search = location.search.replace(`&${RESET_TOKEN_QUERY}=${queryParams.get(RESET_TOKEN_QUERY)}`, '');
    location.search = location.search.replace(`?${RESET_TOKEN_QUERY}=${queryParams.get(RESET_TOKEN_QUERY)}`, '');
    setModalType(null);
    navigate(location, { replace: true });
  };

  const openForgotPasswordModal = (): void => {
    openGlobalModal(GlobalModalTypeEnum.FORGOT_PASSWORD);
    setModalType(GlobalModalTypeEnum.FORGOT_PASSWORD);
  };

  if (!brandingState.settings.id) {
    return null;
  }

  return (
    <Box
      sx={{
        ...sx.wrapper,
        backgroundImage: brandingState?.settings.backgroundImage
          ? `url(${brandingState?.settings.backgroundImage})`
          : sx.wrapper.backgroundImage,
      }}
    >
      <Header
        bgColor={brandingState?.settings.bgColor}
        logo={brandingState?.settings?.loginLogo}
        buttonsColor={brandingState?.settings.actionColor}
        openGlobalModal={openGlobalModal}
      />
      <Box component="main" sx={{ flexGrow: 1 }} py={5}>
        <Container>
          <Typography
            component="h1"
            variant="h3"
            fontWeight={600}
            mb={5}
            mt={4.5}
            color={brandingState?.settings.fontColor || 'custom.neutrals.scale.400'}
          >
            <div dangerouslySetInnerHTML={{ __html: brandingState?.settings?.description || '' }}></div>
          </Typography>
          <Button
            size="large"
            variant="contained"
            color="primary"
            sx={{ background: brandingState?.settings.actionColor }}
            onClick={(): void => openGlobalModal(GlobalModalTypeEnum.SIGN_UP)}
          >
            {translate('Become an affiliate')}
          </Button>
        </Container>
      </Box>
      <Footer
        termsAndConditions={brandingState?.settings.terms}
        bgColor={brandingState?.settings.bgColor}
        email={brandingState?.settings.email}
        fontColor={brandingState?.settings.fontColor}
        downloadApkUrl={brandingState?.settings.meta?.apkUrl}
      />
      {modalType === GlobalModalTypeEnum.SIGN_UP && <SignUpFormModal title="Sign up" onClose={closeModal} />}
      {modalType === GlobalModalTypeEnum.SIGN_IN && (
        <SignInFormModal title="Sign In" onClose={closeModal} openForgotPasswordModal={openForgotPasswordModal} />
      )}
      {modalType === GlobalModalTypeEnum.FORGOT_PASSWORD && <ForgotPasswordModal onClose={closeModal} />}
      {modalType === GlobalModalTypeEnum.SET_NEW_PASSWORD && queryParams.get(RESET_TOKEN_QUERY) && (
        <SetNewPasswordModal onClose={closeModal} resetToken={queryParams.get(RESET_TOKEN_QUERY) || ''} />
      )}
    </Box>
  );
};

export default Landing;
